import { strToU8, zipSync } from 'fflate';
import { getSplitByHour, getSplitByWeekday, getSplitByMonth, getSplitByYear, getTotalAudience } from '../assets/libs/broox/brooxCloudApi';
import { AccountParams } from './AccountParams';
import { Filter, FilterService } from './Filter';
import { MediaPlaybackAggregate as MediaPlayback } from './MediaPlayback';

interface WorkbookSheet {
  name: string,
  rows: (string | number)[][]
}

interface WorkbookDownload {
  name: string,
  content: Uint8Array
}

const getExcelWorkbook = async (account: string, filter: Filter, params: AccountParams, mediaPlayback?: MediaPlayback): Promise<WorkbookDownload> => {
  const f = FilterService.parse(filter);
  const audience = await getTotalAudience(f, params.host, params.token || params.id, params.apiKey);
  console.log('audience', audience);
  const historicByHour = await getSplitByHour('audience', filter, params.host, params.token || params.id, params.apiKey);
  const historicByWeekday = await getSplitByWeekday('audience', filter, params.host, params.token || params.id, params.apiKey);
  const historicByMonth = await getSplitByMonth('audience', filter, params.host, params.token || params.id, params.apiKey);
  const historicByYear = await getSplitByYear('audience', filter, params.host, params.token || params.id, params.apiKey);
  const header = ['Account', 'Location', 'Installation', 'Campaign', 'From', 'To', 'When', 'Traffic', 'Watchers', 'Dwell', 'Attention', 'Children', 'Young', 'Middle Age', 'Elder', 'Female', 'Male', 'Total Traffic', 'Total Watchers', 'Total Dwell', 'Total Attention', 'Total Children', 'Total Young', 'Total Middle Age', 'Total Elder', 'Total Female', 'Total Male'];
  const byHour = getData(account, filter, audience, historicByHour);
  const byWeekday = getData(account, filter, audience, historicByWeekday);
  const byMonth = getData(account, filter, audience, historicByMonth);
  const byYear = getData(account, filter, audience, historicByYear);

  const sheets = [
    { name: 'ByHour', rows: [header, ...byHour] },
    { name: 'ByWeekday', rows: [header, ...byWeekday] },
    { name: 'ByMonth', rows: [header, ...byMonth] },
    { name: 'ByYear', rows: [header, ...byYear] },
    { name: 'Media Playback', rows: getMediaPlaybackRows(mediaPlayback) },
  ];
  return {
    name: `${getFileName(account, filter)}.xlsx`,
    content: createWorkbook(sheets)
  };
}

const getData = (account: string, filter: Filter, audience: any, historic: any): any[][] => {
  const lines = [];
  const filterData = getFilterData(account, filter);
  const genderTotal = audience.gender.reduce((accumulator: number, current: any) => accumulator + current.value, 0);
  const genderMale = getGenderValue(audience.gender, 'Male');
  const genderFemale = getGenderValue(audience.gender, 'Female');
  const ageTotal = audience.age.reduce((accumulator: number, current: any) => accumulator + current.value, 0);
  for(let i = 0; i < historic.traffic.length; i++) {
    const traffic = historic.traffic[i];
    const views = Math.round(historic.views[i].value);
    const dwell = Math.round(historic.dwell[i].value);
    const attention = Math.round(historic.attention[i].value);
    const age = historic.age[i];
    const gender = historic.gender[i];
    const historicAgeTotal = age.values.reduce((accumulator: number, current: any) => accumulator + current.value, 0);
    const historicGenderTotal = gender.values.reduce((accumulator: number, current: any) => accumulator + current.value, 0);
    const line = [...filterData, traffic.label, Math.round(traffic.value), views, dwell, attention, getPercentage(age.values[0].value, historicAgeTotal), getPercentage(age.values[1].value, historicAgeTotal), getPercentage(age.values[2].value, historicAgeTotal), getPercentage(age.values[3].value, historicAgeTotal), getPercentage(getGenderValue(gender.values, 'Female'), historicGenderTotal), getPercentage(getGenderValue(gender.values, 'Male'), historicGenderTotal), Math.round(audience.traffic.count), Math.round(audience.views.count), Math.round(audience.dwell.average), Math.round(audience.attention.average), getPercentage(audience.age[0].value, ageTotal), getPercentage(audience.age[1].value, ageTotal), getPercentage(audience.age[2].value, ageTotal), getPercentage(audience.age[3].value, ageTotal), getPercentage(genderFemale, genderTotal), getPercentage(genderMale, genderTotal)];
    lines.push(line);
  }
  return lines;
}

const getFilterData = (account: string, filter: Filter): string[] => {
  const data = [account];
  data.push(FilterService.getLocationNames(filter) || '-');
  data.push(filter.installationName ? filter.installationName : '-');
  data.push(filter.campaignName ? filter.campaignName : '-');
  data.push(filter.from ? `${filter.from.getFullYear()}-${filter.from.getMonth() + 1}-${filter.from.getDate()}` : '-');
  data.push(filter.to ? `${filter.to.getFullYear()}-${filter.to.getMonth() + 1}-${filter.to.getDate()}` : '-');
  return data;
}

const getPercentage = (value: number, total: number) => {
  return !total || !value ? '0%' : `${(value / total * 100).toFixed(1)}%`;
}

const getFileName = (account: string, filter: Filter) => {
  let fileName = `Broox_Export_${account}_`;
  FilterService.getLocationNames(filter) && (fileName += `${FilterService.getLocationNames(filter)}_`);
  filter.installationName && (fileName += `${filter.installationName}_`);
  filter.campaignName && (fileName += `${filter.campaignName}_`);
  filter.from && (fileName += `${filter.from.getFullYear()}-${filter.from.getMonth() + 1}-${filter.from.getDate()}_`);
  filter.to && (fileName += `${filter.to.getFullYear()}-${filter.to.getMonth() + 1}-${filter.to.getDate()}_`);
  return fileName;
}

const getGenderValue = (values: any[], label: string): number => {
  const value = values.find(v => v.label === label);
  return value ? value.value : 0;
}

const getMediaPlaybackRows = (mediaPlayback?: MediaPlayback): (string | number)[][] => {
  const header = ['Media', 'Campaign', 'Playlist', 'Played', 'Views', 'Partially', 'Fully'];
  const rows = mediaPlayback?.list.map(item => [
    item.name,
    item.campaign,
    item.playlist,
    item.noViews,
    item.fullViews + item.partialViews,
    item.partialViews,
    item.fullViews
  ]) || [];

  return [header, ...rows];
}

const createWorkbook = (sheets: WorkbookSheet[]): Uint8Array => {
  const files: Record<string, Uint8Array> = {
    '[Content_Types].xml': xmlToU8(getContentTypes(sheets.length)),
    '_rels/.rels': xmlToU8(getRootRels()),
    'xl/workbook.xml': xmlToU8(getWorkbook(sheets)),
    'xl/_rels/workbook.xml.rels': xmlToU8(getWorkbookRels(sheets.length)),
    'xl/styles.xml': xmlToU8(getStyles())
  };

  sheets.forEach((sheet, index) => {
    files[`xl/worksheets/sheet${index + 1}.xml`] = xmlToU8(getWorksheet(sheet.rows));
  });

  return zipSync(files);
}

const xmlToU8 = (xml: string): Uint8Array => strToU8(xml);

const getContentTypes = (sheetCount: number): string => {
  const sheetOverrides = Array.from({ length: sheetCount }, (_, index) =>
    `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
  ).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
  <Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
  <Default Extension="xml" ContentType="application/xml"/>
  <Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
  <Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
  ${sheetOverrides}
</Types>`;
}

const getRootRels = (): string => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  <Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`;

const getWorkbook = (sheets: WorkbookSheet[]): string => {
  const workbookSheets = sheets.map((sheet, index) =>
    `<sheet name="${escapeXml(sanitizeSheetName(sheet.name))}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`
  ).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
  <sheets>${workbookSheets}</sheets>
</workbook>`;
}

const getWorkbookRels = (sheetCount: number): string => {
  const sheetRels = Array.from({ length: sheetCount }, (_, index) =>
    `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
  ).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
  ${sheetRels}
  <Relationship Id="rId${sheetCount + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`;
}

const getStyles = (): string => `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <fonts count="1"><font><sz val="11"/><name val="Calibri"/></font></fonts>
  <fills count="1"><fill><patternFill patternType="none"/></fill></fills>
  <borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>
  <cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
  <cellXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/></cellXfs>
  <cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;

const getWorksheet = (rows: (string | number)[][]): string => {
  const xmlRows = rows.map((row, rowIndex) => {
    const rowNumber = rowIndex + 1;
    const cells = row.map((value, columnIndex) => getCell(value, columnIndex, rowNumber)).join('');
    return `<row r="${rowNumber}">${cells}</row>`;
  }).join('');

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
  <sheetData>${xmlRows}</sheetData>
</worksheet>`;
}

const getCell = (value: string | number, columnIndex: number, rowNumber: number): string => {
  const reference = `${getColumnName(columnIndex)}${rowNumber}`;
  if(typeof value === 'number' && Number.isFinite(value)) {
    return `<c r="${reference}"><v>${value}</v></c>`;
  }
  return `<c r="${reference}" t="inlineStr"><is><t>${escapeXml(value.toString())}</t></is></c>`;
}

const getColumnName = (index: number): string => {
  let columnName = '';
  let number = index + 1;
  while(number > 0) {
    const remainder = (number - 1) % 26;
    columnName = String.fromCharCode(65 + remainder) + columnName;
    number = Math.floor((number - 1) / 26);
  }
  return columnName;
}

const sanitizeSheetName = (name: string): string => {
  const sanitized = name.replace(/[\\/?*[\]:]/g, '').trim();
  return (sanitized || 'Sheet').slice(0, 31);
}

const escapeXml = (value: string): string => {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

export { getExcelWorkbook };
